<template>
  <div class="basic-gray-section f-h issues" style="overflow: auto;">
    <LeftDrawerPanel class="issues__filters">
      <h1 class="basic-blue-title" style="text-align: left; background: transparent; padding-top: 0px;">
        {{ $t('message.SORT') }}
      </h1>
      <RadioGroup
        v-model="jiraIssuesSortBy"
        class="issues__filters__radios"
        :items="sortOptions"
        sortable
      />
      <h1 class="basic-blue-title" style="text-align: left; background: transparent; padding-top: 0px;">
        {{ $t('message.DISPLAY') }}
      </h1>
      <CheckboxList
        v-model="selectedStatusIds"
        :items="statusTemplates"
      />
    </LeftDrawerPanel>
    <div class="issues__top-container f-w">
      <div class="issues__top-container__buttons">
        <button class="issues__new-issue action-btn v-centerer" @click="displayReporter">
          <Icon>{{ addExtraBold }}</Icon>
          <span>{{ $t('message.ISSUE_NEW_ISSUE') }}</span>
        </button>
        <Switcher
          v-model="categoryChoice"
          class="switcher"
          :titles="categories"
        />
      </div>
      <div class="status">
        <span class="status__element status__element--pending">{{ $t('message.ISSUE_PENDING') }}</span>
        <span class="status__element status__element--on-going">{{ $t('message.ISSUE_ON_GOING') }}</span>
        <span class="status__element status__element--finished">{{ $t('message.ISSUE_FINISHED') }}</span>
        <span class="status__element status__element--refused">{{ $t('message.ISSUE_REFUSED') }}</span>
      </div>
    </div>
    <div v-if="allIssues">
      <RecycleScroller
        v-slot="{ item }"
        class="issues__scroller"
        :items="displayIssues"
        :item-size="issueItemSize"
        key-field="id"
      >
        <JiraIssueItem :key="item.id" :issue="item" />
      </RecycleScroller>
      <LoadingOverlay v-model="issuesLoading" />
    </div>
  </div>
</template>

<script>
/**
 * Display all company issues
 *
 */

import { isEqual, cloneDeep } from 'lodash';
// Components
import { RecycleScroller } from 'vue-virtual-scroller';
import CheckboxList from '@/components/checkbox-list.vue';
import Icon from '@/components/icon.vue';
import JiraIssueItem from '@/components/jira-issue-item.vue';
import LeftDrawerPanel from '@/components/left-drawer-panel.vue';
import LoadingOverlay from '@/components/loading-overlay.vue';
import RadioGroup from '@/components/radio-group.vue';
import Switcher from '@/components/switcher.vue';
// Utils
import { addExtraBold } from '@/assets/icons';
// States
import { FETCH_JIRA_ISSUES } from '@/store/modules/jira-issues/action-types';

export default {

  name: 'JiraIssues',

  components: {
    CheckboxList,
    Icon,
    JiraIssueItem,
    LeftDrawerPanel,
    LoadingOverlay,
    RadioGroup,
    RecycleScroller,
    Switcher,
  },

  data() {
    return {
      addExtraBold,
      categories: [
        this.$t('message.ISSUE_BUGS'),
        this.$t('message.ISSUE_IMPROVEMENTS'),
      ],
      buttonParams: { value: 0, desc: true },
      sortOptions: [
        { id: 0, label: this.$t('message.ISSUE_CREATION_DATE') },
        { id: 1, label: this.$t('message.JIRA_DESCRIPTION') },
      ],
      statusTemplates: [
        { id: 0, label: this.$t('message.ISSUE_PENDING') },
        { id: 1, label: this.$t('message.ISSUE_ON_GOING') },
        { id: 2, label: this.$t('message.ISSUE_FINISHED') },
        { id: 3, label: this.$t('message.ISSUE_REFUSED') },
      ],
    };
  },

  computed: {
    allIssues() {
      return Object.values(this.$store.state.jiraIssues.issues.data);
    },
    issuesLoading() {
      return this.$store.state.jiraIssues.issues.issuesLoading;
    },
    /** Number: Type of issues to display: 0 for bugs, 1 for features */
    categoryChoice: {
      get() {
        return Number(this.$route.query.jiraIssuesCategory) || 0;
      },
      set(val) {
        const query = Object.assign({}, this.$route.query);
        query.jiraIssuesCategory = String(val);
        if (!isEqual(query, this.$route.query)) {
          this.$router.replace({ query });
        }
      },
    },
    /** Object: to pass a RadioGroup value to sort issues */
    jiraIssuesSortBy: {
      get() {
        let jiraIssuesSortBy = 0;
        const jiraIssuesSortDesc = this.$route.query.jiraIssuesSortDesc !== 'false';
        if (this.$route.query.jiraIssuesSortBy !== undefined) {
          jiraIssuesSortBy = Number(this.$route.query.jiraIssuesSortBy)
            .sBetweenBoundaries(0, this.sortOptions.length - 1);
        }
        return { value: jiraIssuesSortBy, desc: jiraIssuesSortDesc };
      },
      set(jiraIssuesSortBy) {
        if (jiraIssuesSortBy.value !== undefined && jiraIssuesSortBy.desc !== undefined) {
          const query = Object.assign({}, this.$route.query);
          query.jiraIssuesSortDesc = jiraIssuesSortBy.desc ? 'true' : 'false';
          query.jiraIssuesSortBy = Number(jiraIssuesSortBy.value).sBetweenBoundaries(0, this.sortOptions.length - 1);
          if (!isEqual(query, this.$route.query)) {
            this.$router.replace({ query });
          }
        }
      },
    },
    /** Array: Array of bugs reported by this company */
    bugIssues() {
      return this.filteredIssues.filter(issue => issue.fields.issuetype.name === 'Customer Bug');
    },
    /** Array: Array of feature suggestions reported by this company */
    featureIssues() {
      return this.filteredIssues.filter(issue => issue.fields.issuetype.name === 'Customer Task');
    },
    /** Array: Issues to display according to the selected category */
    displayIssues() {
      if (this.$route.query.jiraIssuesCategory === '0' || !this.$route.query.jiraIssuesCategory) {
        return this.bugIssues;
      }
      return this.featureIssues;
    },
    /** Array: Array of issues filtered according to the user-selected filters on the page */
    filteredIssues() {
      // Filter to display only issues with an description
      const allIssuesClone = cloneDeep(this.allIssues).filter(issue => issue.description);
      let issues;
      if (this.selectedStatusIds.length) {
        issues = allIssuesClone.filter(issue => this.selectedStatusIds.indexOf(issue.statusId) > -1);
      } else {
        issues = [];
      }
      switch (this.jiraIssuesSortBy.value) {
        case 0:
          return !this.jiraIssuesSortBy.desc
            ? issues.sort((a, b) => a.fields.calculatedCreatedDate - b.fields.calculatedCreatedDate)
            : issues.sort((a, b) => b.fields.calculatedCreatedDate - a.fields.calculatedCreatedDate);
        case 1:
          return !this.jiraIssuesSortBy.desc
            ? issues.sort((a, b) => String(a.description).localeCompare(b.description))
            : issues.sort((a, b) => String(b.description).localeCompare(a.description));
        default:
          return issues;
      }
    },
    /** Array: Aray of status IDs usedto filter the list of issues */
    selectedStatusIds: {
      get() {
        const ids = this.$route.query.jiraIssuesStatusFilter;
        if (ids === '') {
          return [];
        }
        if (ids) {
          const sIds = ids.split(',').map(id => Number(id));
          return this.statusTemplates.filter(s => sIds.includes(s.id)).map(s => s.id);
        }
        return this.statusTemplates.map(s => s.id);
      },
      set(newIds) {
        const query = Object.assign({}, this.$route.query, { jiraIssuesStatusFilter: newIds.join(',') });
        if (!isEqual(query, this.$route.query)) {
          this.$router.replace({ query });
        }
      },
    },
    /** Number: Size in pixels of each issue item. 5em according to CSS rules + 0.5em margin */
    issueItemSize() {
      const { fontSize } = this.$store.getters;
      return fontSize * 5.5;
    },
  },

  created() {
    this.refresh();
  },

  methods: {
    categoryUpdated(index) {
      const query = Object.assign({}, this.$route.query);
      query.jiraIssuesCategory = index;
      this.$router.replace({ query });
    },
    displayReporter() {
      const query = Object.assign({}, this.$route.query);
      query.jiraIssuesReporter = true;
      this.$router.replace({ query });
    },
    refresh() {
      if (!this.issuesLoading) {
        this.$store.dispatch(`jiraIssues/${FETCH_JIRA_ISSUES}`);
      }
    },
  },
};
</script>

<style lang="scss">
@import '@/assets/scss/vars.scss';

  .issues {

    &__filters {

      &__radios {
        margin-bottom: 2em;
      }
    }

    &__top-container {
      display: flex;
      justify-content: space-between;
      align-items: center;
      box-sizing: border-box;
      background-color: $h1-background;
      padding: 20px 0;

      &__buttons {
        display: flex;
        justify-content: space-between;
        width: 50%;
      }
    }

    &__scroller {
      height: 100%;
    }

    &__new-issue {
      margin-left: 3em;
    }

    .status {

      &__element {
        position: relative;
        display: inline-block;
        margin-right: 30px;

        &::before {
          content: '';
          width: 1em;
          height: 1em;
          position: absolute;
          left: -20px;
          top: 50%;
          transform: translateY(-50%);
          border-radius: 10px;
        }

        &--pending::before {
          background-color: $color-jira-pending;
        }

        &--on-going::before {
          background-color: $color-jira-on-going;
        }

        &--finished::before {
          background-color: $color-jira-finished;
        }

        &--refused::before {
          background-color: $color-jira-refused;
        }

        &:last-child {
          margin-right: 20px;
        }
      }
    }
    @media (max-width: $smartphone-landscape) {
      &__top-container {
        flex-direction: column;
        align-items: center;
        justify-content: center;
        padding-bottom: 0;

        &__buttons {
          display: flex;
          align-items: center;
        }

        & > * {
          margin-bottom: 10px;
        }

        button > span, svg {
          margin-bottom: 0;
        }

        .switcher {
          margin-bottom: 0;
        }
      }

      .status {
        padding-left: 3em;
      }

      &__new-issue {
        margin-left: 0;
        margin-right: 1em;
      }
    }

    @media (max-width: $smartphone-portrait) {
      &__top-container {

        &__buttons {
          display: flex;
          align-items: center;
          flex-direction: column;
        }

        & * {
          margin-bottom: 10px;
        }

        .switcher {
          margin-bottom: 0;
        }
      }
    }
  }

</style>
